// ─────────────────────────────────────────────────────────────────────────────
// AI Email Triage engine — 1:1 port of the monolith triage system (index.html
// ~L20277-21174): projectsTriageDemo (RAVEn flagship), the data-driven
// TRIAGE_EMAILS engine (7 inbox scenarios), and the Apply / Skip / Undo /
// Apply-all recommendation pipeline that propagates real artifacts across the
// app (Jira tickets, My-Tasks, Risk register, Correspondence log, and clickable
// project-history entries).
//
// Port strategy (React foundations):
//   • Modal        → window.openMo / window.closeMo (the bridged #mo host).
//   • Cross-page   → writes straight into window.__WS_JIRA / __WS_TASKS /
//     mutations       __WS_RISKS / __WS_CORRESPONDENCE, the arrays My Workspace's
//                     picker actually reads (see _wsArray below). ATLAS has no
//                     "My Projects" store, so window.PROJECTS is always empty —
//                     project-linked bookkeeping (history, related-items) is
//                     skipped gracefully rather than blocking the apply.
//   • Clicks       → ATLAS sends `script-src-attr 'none'`, so the browser never
//     auto-executes an inline onclick="" attribute (the monolith's original
//     mechanism). Generated HTML below marks actionable elements with
//     data-tc="<fn name>" data-tc-args="<JSON array>" instead, and one
//     delegated listener (_installTriageClickDelegation) dispatches through
//     the same window-global functions installTriageEngine() attaches.
//
// NOTE (sharedChangesNeeded): the store exposes no remove/update actions, so
//   Undo splices the mirrored window.WS_* arrays in place (data stays correct,
//   matching the monolith which also mutates arrays directly) and reverts the
//   project via updateProject; a live re-render of OTHER pages on Undo would
//   need removeJira/removeTask/removeWsRisk/removeCorrespondence/updateWsRisk
//   store actions. The RAVEn "risk" rec likewise updates R-HSI-007 in place.
// ─────────────────────────────────────────────────────────────────────────────

import { pbAuthedFetch } from '../authHeaders';

function demoStamp(): string { return new Date().toISOString().slice(0, 10); }

// react-router's BrowserRouter listens for popstate to re-match the route, but
// history.pushState() alone never fires one (only real back/forward does) —
// so a plain pushState from outside a <Link>/navigate() call updates the URL
// bar without the app re-rendering. Dispatching a synthetic popstate after
// the pushState is the standard workaround and keeps this a same-tab, no-reload
// navigation, which matters here because window.__WS_TASKS/__WS_JIRA/etc are
// in-memory only — a full-page nav (location.href) would wipe the very item
// this is trying to show before My Workspace could read it.
function _pbNavigate(path: string): void {
  if (typeof window === 'undefined') return;
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

// ── Interfaces ─────────────────────────────────────────────────────────────────
interface Project {
  id: string;
  name: string;
  history?: HistoryEntry[];
  relatedItems?: RelatedItems;
  criticalPath?: CriticalPathMilestone[];
  [key: string]: any;
}

interface HistoryEntry {
  date: string;
  type: string;
  actor: string;
  actorRole: string;
  content: string;
  source: string;
  sourceIcon: string;
  embedded?: EmbeddedItem[] | null;
  _triageKey?: string;
}

interface RelatedItems {
  jira?: string[];
  tasks?: string[];
  risks?: string[];
  correspondence?: string[];
  [key: string]: string[] | undefined;
}

interface CriticalPathMilestone {
  name: string;
  date: string;
  upcomingDetails?: {
    summary?: string;
    source?: string;
    sourceIcon?: string;
    owner?: string;
  };
  [key: string]: any;
}

interface EmbeddedItem {
  kind: string;
  id: string;
}

interface DetectedItem {
  type: string;
  ico: string;
  label: string;
  desc: string;
}

interface MutationJira {
  id: string;
  summary: string;
  type: string;
  priority: string;
  status: string;
  assignee: string;
  points?: number;
}

interface MutationTask {
  id: string;
  title: string;
  due: string;
  priority: string;
  src?: string;
  appliedActions?: any[];
}

interface MutationRisk {
  id: string;
  title: string;
  score: number;
  level: string;
  trend: string;
  owner: string;
  category: string;
  appliedActions?: any[];
}

interface MutationCorrespondence {
  id: string;
  channel: string;
  to: string;
  subject: string;
  body: string;
}

interface MutationProjectNote {
  field: string;
  text: string;
}

interface Mutations {
  newJira?: MutationJira;
  newTask?: MutationTask;
  newRisk?: MutationRisk;
  correspondence?: MutationCorrespondence;
  projectNote?: MutationProjectNote;
}

interface Recommendation {
  letter: string;
  title: string;
  detail: string;
  impact: string[];
  mutations: Mutations;
  apply?: string;
}

interface TriageEmail {
  targetProject: string | null;
  modalTitle: string;
  flagged: string;
  from: string;
  subject: string;
  bodyHtml: string;
  detected: DetectedItem[];
  recs: Recommendation[];
}

interface UndoRecord {
  tasks: string[];
  risks: string[];
  jira: string[];
  corr: string[];
  projField: string | null;
  projFieldOld: any;
  projId: string;
}

interface ActivityRow {
  status: string;
  label: string;
  detail: string;
  dur: string;
}

interface LiveTriageEmail {
  from?: string;
  subject?: string;
  body?: string;
  snippet?: string;
}

interface LiveTriageRecommendation {
  type?: string;
  title?: string;
  summary?: string;
  detail?: string;
  priority?: string;
  owner?: string;
  due?: string;
}

interface LiveTriageResponse {
  ok: boolean;
  targetProject?: string | null;
  detected?: Array<{ type?: string; label?: string; desc?: string }>;
  recommendations?: LiveTriageRecommendation[];
}

// ── Constants ──────────────────────────────────────────────────────────────────
const SPARK_SVG = '<span class="ico"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M8 1.5l1.5 4 4 1.5-4 1.5L8 12.5 6.5 8.5 2.5 7l4-1.5z"/><path d="M13 11.5l.6 1.6 1.6.6-1.6.6-.6 1.6-.6-1.6-1.6-.6 1.6-.6z" fill="currentColor"/></svg></span>';
const CHECK_SVG = '<span class="ico"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M2.5 8.5L6 12l7.5-8"/></svg></span>';
const BOLT_SVG = '<span class="ico"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M9 1.5L3 9h4l-1 5.5L13 7H9l1-5.5z" fill="currentColor" stroke="none"/></svg></span>';

function pbEsc(s: any): string {
  return String(s == null ? '' : s).replace(/[&<>"']/g, function (c: string) {
    return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' } as Record<string, string>)[c];
  });
}

// ATLAS sends `script-src-attr 'none'` (helmet's CSP default), which blocks the
// browser from ever auto-executing an inline onclick="" attribute — the modal
// content below used to rely on exactly that (ported verbatim from
// dev_projectbuilder, which had no such CSP). Real clicks on Apply/Skip/Undo/
// etc silently did nothing; only calling the window-exposed function directly
// (bypassing the attribute entirely) worked. Fix: mark clickables with
// data-tc="<window function name>" data-tc-args="<JSON array>" instead of
// onclick, and dispatch through one delegated listener — a real addEventListener
// call, not eval, so it isn't blocked by script-src either.
function _tc(name: string, ...args: Array<string | number>): string {
  return `data-tc="${pbEsc(name)}" data-tc-args="${pbEsc(JSON.stringify(args))}"`;
}

// Same as _tc, for rewiring an already-mounted element's action in place
// (Apply → Undo, Undo → Apply) instead of setAttribute('onclick', ...).
function _setTc(el: Element, name: string, ...args: Array<string | number>): void {
  el.setAttribute('data-tc', name);
  el.setAttribute('data-tc-args', JSON.stringify(args));
}

function _installTriageClickDelegation(): void {
  if (typeof document === 'undefined' || (window as any).__pbTcDelegated) return;
  (window as any).__pbTcDelegated = true;
  // Capture phase, not bubble: .mo-card's onClick calls e.stopPropagation() to
  // keep card clicks from reaching .mo-overlay's close-on-backdrop-click handler,
  // and React's synthetic stopPropagation() also stops the underlying native
  // event from continuing to bubble past the React root — so a bubble-phase
  // listener on document would never see clicks from inside the modal at all.
  // Capture runs top-down before that happens, so it isn't affected.
  document.addEventListener('click', (ev) => {
    const target = (ev.target as HTMLElement | null)?.closest?.('[data-tc]') as HTMLElement | null;
    if (!target) return;
    const name = target.getAttribute('data-tc');
    const fn = name ? (window as any)[name] : null;
    if (typeof fn !== 'function') return;
    const argsRaw = target.getAttribute('data-tc-args');
    let args: unknown[] = [];
    if (argsRaw) { try { args = JSON.parse(argsRaw); } catch { /* malformed — call with no args */ } }
    fn(...args);
  }, true);
}

function _hhmm(): string {
  return String(new Date().getHours()).padStart(2, '0') + String(new Date().getMinutes()).padStart(2, '0');
}
void _hhmm; // keep available for future use

function _proj(id: string): Project | null {
  return ((window as any).PROJECTS || []).find((p: Project) => p.id === id) || null;
}

function _patchProject(id: string, patch: Record<string, any>): void {
  if ((window as any).pbStore && typeof (window as any).pbStore.updateProject === 'function') (window as any).pbStore.updateProject(id, patch);
}

function _cloneRelated(rel: RelatedItems | undefined | null): RelatedItems | null {
  if (!rel) return null;
  const out: RelatedItems = {};
  Object.keys(rel).forEach((k) => { (out as any)[k] = Array.isArray((rel as any)[k]) ? (rel as any)[k].slice() : (rel as any)[k]; });
  return out;
}

// ── clickable embed pill (verbatim port of index.html:19821 _embedPillHtml) ──
function _embedPillHtml(item: EmbeddedItem): string {
  if (!item) return '';
  if (item.kind === 'jira') return `<span class="embed-pill jira" ${_tc('triageOpenItem', 'jira', item.id)}><span class="embed-pill-ico"><span class="ico"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="5" cy="8" r="3.5"/><circle cx="11" cy="8" r="3.5"/></svg></span></span>${item.id}</span>`;
  if (item.kind === 'task') return `<span class="embed-pill task" ${_tc('triageOpenItem', 'task', item.id)}><span class="embed-pill-ico"><span class="ico"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><rect x="2.5" y="2.5" width="11" height="11" rx="1.5"/><path d="M5 8l2 2 4-4"/></svg></span></span>${item.id}</span>`;
  if (item.kind === 'risk') return `<span class="embed-pill risk" ${_tc('triageOpenItem', 'risk', item.id)}><span class="embed-pill-ico"><span class="ico"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M8 1.5L15 14H1z"/><path d="M8 6v4"/><circle cx="8" cy="12" r=".6" fill="currentColor"/></svg></span></span>${item.id}</span>`;
  if (item.kind === 'correspondence') {
    const c = (typeof (window as any).__WS_CORRESPONDENCE !== 'undefined') ? (window as any).__WS_CORRESPONDENCE.find((x: any) => x.id === item.id) : null;
    const label = c ? (c.channel === 'Outlook Email' ? '<span class="ico"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M2 4h12v8H2z"/><path d="M2 4l6 4 6-4"/></svg></span> Email' : c.channel === 'Teams' ? '<span class="ico"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M8 2L13 8 8 14 3 8z" fill="currentColor"/></svg></span> Teams' : '<span class="ico"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M2 14h3L13 6l-3-3-8 8z"/><path d="M10 3l3 3"/></svg></span> Message') : 'Correspondence';
    const subj = c ? ' · ' + c.subject.substring(0, 32) + (c.subject.length > 32 ? '…' : '') : '';
    return `<span class="embed-pill correspondence" ${_tc('triageOpenItem', 'correspondence', item.id)}>${label}${subj}</span>`;
  }
  return '';
}

// ── store-routed injectors ─────────────────────────────────────────────────
// WorkspacePage (My Workspace) reads its Jira/Task/Risk picker data straight
// off window.__WS_JIRA / __WS_TASKS / __WS_RISKS (see its dsJira/dsTasks/
// dsRisks). These used to route through window.pbStore.addJiraTicket() etc,
// a dev_projectbuilder global that was never ported to ATLAS — so every
// "Apply recommendation" silently produced nothing anyone could see. Writing
// straight into the arrays the real page reads is the actual connection.
function _wsArray(key: string): any[] {
  const w = window as any;
  if (!Array.isArray(w[key])) w[key] = [];
  return w[key];
}
function _injectJira(j: MutationJira): void {
  const list = _wsArray('__WS_JIRA');
  if (!list.find((x: any) => x.id === j.id)) {
    list.push({ id: j.id, summary: j.summary, type: j.type, priority: j.priority, status: j.status, assignee: j.assignee });
  }
}
// Live Jira create (UX wiring): when the Jira connector is configured, an applied
// AI-triage recommendation ALSO files a real issue through the same single-issue
// endpoint the board's Create-ticket dialog and Convert-to-Tasker use
// (POST /api/integrations/jira/issue, label ai-triage). Fire-and-audit: the local
// store ticket stays the console's record and Undo removes it alone — a real filed
// issue is never silently deleted (matching Convert-to-Tasker, which has no undo).
// Connector-off (400) stays silent so the demo flow is byte-identical.
function _fileLiveJira(j: MutationJira, source: string): void {
  try {
    pbAuthedFetch('/api/integrations/jira/issue', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ summary: j.summary, description: 'Filed by Stella Triage (' + source + ') from Stella.', issueType: j.type === 'Bug' ? 'Bug' : 'Task', labels: ['ai-triage'] }),
    }).then(async (res) => {
      if (res.status === 400) return; // connector not configured — demo flow stands
      const d = await res.json().catch(() => ({}));
      const key = d.key || (d.issue && d.issue.key) || d.id;
      if (res.ok && d.ok !== false && key) {
        if (typeof (window as any).pbToast === 'function') (window as any).pbToast('Filed live in Jira: ' + key, 'ok');
        if (typeof (window as any).appendActivityRow === 'function') (window as any).appendActivityRow({ status: 'ok', label: 'Stella Triage · live Jira issue ' + key, detail: String(j.summary || '').substring(0, 80), dur: 'Jira · live' });
      } else if (typeof (window as any).pbToast === 'function') {
        (window as any).pbToast('Live Jira create failed: ' + (d.error || res.status) + ' — local record kept', 'err');
      }
    }).catch(() => { /* network — local record kept */ });
  } catch (_err) { /* never break the local apply */ }
}
function _removeJira(id: string): void {
  const l: any[] = (window as any).__WS_JIRA;
  if (l) { const i = l.findIndex((x: any) => x.id === id); if (i >= 0) l.splice(i, 1); }
}
function _injectTask(tk: MutationTask, src: string, appliedAction?: any): void {
  const list = _wsArray('__WS_TASKS');
  if (!list.find((t: any) => t.id === tk.id)) {
    const task: any = { id: tk.id, title: tk.title, due: tk.due, priority: tk.priority, src: src };
    if (appliedAction) task.appliedActions = [appliedAction];
    list.push(task);
  }
}
function _removeTask(id: string): void {
  const l: any[] = (window as any).__WS_TASKS;
  if (l) { const i = l.findIndex((t: any) => t.id === id); if (i >= 0) l.splice(i, 1); }
}
function _injectRisk(rk: MutationRisk, appliedAction?: any): void {
  const list = _wsArray('__WS_RISKS');
  if (!list.find((r: any) => r.id === rk.id)) {
    const risk: any = { id: rk.id, title: rk.title, score: rk.score, level: rk.level, trend: rk.trend, owner: rk.owner, category: rk.category };
    if (appliedAction) risk.appliedActions = [appliedAction];
    list.push(risk);
  }
}
function _removeRisk(id: string): void {
  const l: any[] = (window as any).__WS_RISKS;
  if (l) { const i = l.findIndex((r: any) => r.id === id); if (i >= 0) l.splice(i, 1); }
}
// No dashboard page renders a correspondence list yet, so this keeps its own
// record (for the applied-rec pill / undo) without a real destination to
// write to — unlike jira/task/risk above, which now land on My Workspace.
function _injectCorrespondence(c: MutationCorrespondence, letter: string, projectId: string, today: string): void {
  const list = _wsArray('__WS_CORRESPONDENCE');
  if (!list.find((x: any) => x.id === c.id)) {
    list.push({ id: c.id, date: today, channel: c.channel, to: c.to, subject: c.subject, body: c.body, triggeredBy: 'Stella Triage Rec ' + letter, projectId: projectId });
  }
}
function _removeCorrespondence(id: string): void {
  const l: any[] = (window as any).__WS_CORRESPONDENCE;
  if (l) { const i = l.findIndex((c: any) => c.id === id); if (i >= 0) l.splice(i, 1); }
}

function _activity(row: ActivityRow): void {
  if (typeof (window as any).appendActivityRow === 'function') { try { (window as any).appendActivityRow(row); } catch (_e) { /* no-op */ } }
}
function _openXlModal(title: string, body: string, footer: string): void {
  if (typeof (window as any).openMo === 'function') {
    (window as any).openMo(title, body, footer);
    setTimeout(() => { const card = document.querySelector('.mo-card'); if (card) card.classList.add('xl'); }, 0);
  }
}

// ═════════════════════════════════════════════════════════════════════════════
// TRIAGE_EMAILS — data for the 7 inbox scenarios (verbatim from index.html:20666)
// ═════════════════════════════════════════════════════════════════════════════
const RISK_ICO = '<span class="ico"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M8 1.5L15 14H1z"/><path d="M8 6v4"/><circle cx="8" cy="12" r=".6" fill="currentColor"/></svg></span>';
const ROADBLOCK_ICO = '<span class="ico"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="8" cy="8" r="6.5"/><path d="M3.5 3.5l9 9"/></svg></span>';
const PEOPLE_ICO = '<span class="ico"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M2 4h12M2 8h12M2 12h12"/></svg></span>';

const TRIAGE_EMAILS: Record<string, TriageEmail> = {
  invoice: {
    targetProject: 'PROJ-002', modalTitle: 'Stella Email Triage · April ITPMS invoice',
    flagged: '13 May 2026 · Mon · 0812 EDT', from: 'J. Hartwell, COR <j.hartwell@ice.dhs.gov>',
    subject: 'Apr \u201926 ITPMS invoice ready for COR review — $487,210.44',
    bodyHtml: 'Steve,<br><br>Attaching the April invoice and burn report. Numbers reconcile to the spend plan with a minor variance — about <strong>3.2% under</strong> on labor (two vacancies carried longer than projected) and <strong>$11,400 over</strong> on the Cellebrite line (the STELLAR HORIZON surge).<br><br>I need your sign-off on the variance narrative before I can certify the invoice. Specifically: (a) confirm the labor underrun is timing, not descope; (b) the Cellebrite overage — is that the 8-seat expansion D. Vargas requested, and is it within the ceiling? Need this by COB Wed so I can process before month-end close.<br><br>Jim',
    detected: [
      { type: 'finance', ico: '◈', label: 'Invoice Approval', desc: '$487,210.44 awaiting COR certification' },
      { type: 'schedule', ico: '⧉', label: 'Deadline', desc: 'COB Wed for month-end close' },
      { type: 'risk', ico: RISK_ICO, label: 'Cost Variance', desc: '$11.4K Cellebrite overage vs spend plan' },
      { type: 'personnel', ico: PEOPLE_ICO, label: 'Vacancy Signal', desc: '2 carried vacancies driving labor underrun' },
    ],
    recs: [
      { letter: 'A', title: 'Generate variance narrative · confirm labor underrun is timing', detail: 'Draft the variance narrative: 3.2% labor underrun is a timing variance from two unfilled positions (GS-13 Analyst, GS-12 Specialist) — not a descope. Recruitment in progress; backfill expected by June. Recommend confirming to J. Hartwell so certification can proceed.', impact: ['task · new', 'person · Steve Palmer'],
        mutations: { newTask: { id: 'TK-INV-01', title: 'Variance narrative for Apr invoice · labor underrun = timing, not descope', due: 'Wed COB', priority: 'High' }, correspondence: { id: 'CORR-INV-A', channel: 'Outlook Email', to: 'J. Hartwell (COR)', subject: 'RE: Apr \u201926 ITPMS invoice · variance narrative', body: 'Jim — confirming (a) the 3.2% labor underrun is timing from two open positions (GS-13 Analyst, GS-12 Specialist), not a descope. Recruitment active; backfill projected June. Variance narrative attached for the certification package.' } } },
      { letter: 'B', title: 'File Jira · validate Cellebrite overage against ceiling', detail: 'The $11,400 Cellebrite overage is the 8-seat STELLAR HORIZON expansion. File a Jira task to validate it sits within the contract ceiling and tag it to PROJ-002 so the spend is traceable to an approved action.', impact: ['jira · new', 'project · PROJ-002'],
        mutations: { newJira: { id: 'ITPMS-220', summary: 'Validate Apr Cellebrite $11.4K overage within contract ceiling · STELLAR HORIZON 8-seat', type: 'Task', priority: 'High', status: 'To Do', assignee: 'BL', points: 3 }, projectNote: { field: 'statusDetail', text: 'April invoice reflects $11.4K Cellebrite overage tied to 8-seat STELLAR HORIZON expansion — validation filed as ITPMS-220.' } } },
      { letter: 'C', title: 'Create COR sign-off task · due Wed COB', detail: 'Generate a tracked task for the invoice certification sign-off so it doesn\u2019t slip past the month-end close window. Owner: Steve Palmer. Due: Wed COB. Linked to PROJ-002.', impact: ['task · new', 'sched · hard deadline'],
        mutations: { newTask: { id: 'TK-INV-02', title: 'COR sign-off · April ITPMS invoice certification ($487,210.44)', due: 'Wed COB', priority: 'Highest' } } },
    ],
  },

  cve: {
    targetProject: 'PROJ-005', modalTitle: 'Stella Email Triage · CVE-2026-1847 critical patch',
    flagged: '13 May 2026 · Mon · 0615 EDT', from: 'ICE Cybersecurity — IR Team <ir-team@ice.dhs.gov>',
    subject: '[ADVISORY] Critical patch — CVE-2026-1847 (auth bypass) · all internal apps',
    bodyHtml: 'PRIORITY ADVISORY.<br><br>CVE-2026-1847 is a <strong>critical authentication bypass</strong> (CVSS 9.1) affecting the shared SSO/PIV middleware. Patch window starts <strong>10 May 0200 EDT</strong>. Field Lab impacted systems: Lab gateway, RAVEn web tier, and the ICAM PIV broker.<br><br>All system owners must (1) confirm impacted inventory, (2) schedule the patch in their maintenance window, (3) report completion to the IR team by 12 May 1700. Systems not patched by the deadline will be flagged in the weekly POA&amp;M and may have network access restricted.<br><br>— ICE Cybersecurity IR',
    detected: [
      { type: 'risk', ico: RISK_ICO, label: 'Security Risk', desc: 'CVSS 9.1 auth bypass · PIV broker affected' },
      { type: 'schedule', ico: '⧉', label: 'Hard Deadline', desc: 'Patch + report by 12 May 1700' },
      { type: 'roadblock', ico: ROADBLOCK_ICO, label: 'Compliance', desc: 'Unpatched → POA&M flag + access restriction' },
      { type: 'personnel', ico: PEOPLE_ICO, label: 'Owner Action', desc: 'ICAM PIV broker is PROJ-005 scope' },
    ],
    recs: [
      { letter: 'A', title: 'Log risk R-CVE-1847 · critical patch exposure on PIV broker', detail: 'CVE-2026-1847 directly affects the ICAM PIV broker (PROJ-005 scope). Recommend logging a Critical (score 16) risk so the patch exposure is tracked through completion and visible in the weekly POA&M.', impact: ['risk · new', 'project · PROJ-005'],
        mutations: { newRisk: { id: 'R-CVE-1847', title: 'CVE-2026-1847 auth-bypass exposure on ICAM PIV broker · patch by 12 May', score: 16, level: 'High', trend: 'up', owner: 'B. Liu', category: 'Cybersecurity' }, correspondence: { id: 'CORR-CVE-A', channel: 'Teams', to: 'B. Liu, ICE Cybersecurity IR', subject: 'R-CVE-1847 logged · PIV broker patch tracked', body: 'Logged R-CVE-1847 (Critical, 16) for the CVE-2026-1847 auth-bypass on the ICAM PIV broker. Tracking to the 12 May 1700 patch deadline. Will report completion to IR.' } } },
      { letter: 'B', title: 'File Jira bug · patch ICAM PIV broker · owner B. Liu', detail: 'File a Highest-priority Jira bug for B. Liu to schedule and execute the patch in the next maintenance window, then report completion to the IR team by 12 May 1700. Linked to PROJ-005.', impact: ['jira · new', 'person · B. Liu'],
        mutations: { newJira: { id: 'ITPMS-221', summary: 'Patch ICAM PIV broker for CVE-2026-1847 (auth bypass, CVSS 9.1) · report to IR by 12 May 1700', type: 'Bug', priority: 'Highest', status: 'To Do', assignee: 'BL', points: 5 }, newTask: { id: 'TK-CVE-01', title: 'Patch ICAM PIV broker for CVE-2026-1847 · report to IR by 12 May 1700', due: '12 May 1700', priority: 'Highest' } } },
      { letter: 'C', title: 'Update PROJ-005 schedule note · maintenance window', detail: 'Annotate PROJ-005 so the patch maintenance window is reflected in the project record — keeps the audit trail clean for the POA&M and shows the ICAM migration accommodated the emergency patch.', impact: ['project · PROJ-005', 'sched · maint window'],
        mutations: { projectNote: { field: 'statusDetail', text: 'Emergency CVE-2026-1847 patch on PIV broker scheduled in 11 May maintenance window per ICE Cyber advisory. Tracked as R-CVE-1847 / ITPMS-221.' } } },
    ],
  },

  gao: {
    targetProject: 'PROJ-003', modalTitle: 'Stella Email Triage · GAO-26-308 audit response',
    flagged: '13 May 2026 · Thu · 1402 EDT', from: 'GAO Audit Liaison — K. Park <k.park@gao.gov>',
    subject: 'GAO-26-308 — HSI IT investments oversight · response due 16 May',
    bodyHtml: 'Ms. Null,<br><br>Attaching draft audit findings for fact check. The <strong>Field Lab portfolio is mentioned in 2 of the 7 findings</strong>:<br><br><strong>Finding 3</strong> — inconsistent cost-baseline documentation across the RAVEn and LIMS investments.<br><strong>Finding 5</strong> — gaps in the contractor deliverable acceptance audit trail (ITPMS).<br><br>We need your factual corrections (not management response — that comes later) by <strong>16 May COB</strong>. Please mark any statements you believe are factually inaccurate and provide supporting documentation references.<br><br>Kind regards,<br>K. Park',
    detected: [
      { type: 'schedule', ico: '⧉', label: 'Hard Deadline', desc: 'Fact-check corrections due 16 May COB' },
      { type: 'risk', ico: RISK_ICO, label: 'Audit Exposure', desc: '2 findings touch Field Lab (cost baseline, deliverable trail)' },
      { type: 'roadblock', ico: ROADBLOCK_ICO, label: 'Response Needed', desc: 'PROJ-003 GAO-26-308 Response is the home for this' },
      { type: 'personnel', ico: PEOPLE_ICO, label: 'Assignment', desc: 'Fact-check needs owner + doc references' },
    ],
    recs: [
      { letter: 'A', title: 'Route to PROJ-003 · record the 2 Field Lab findings', detail: 'This fact-check is the active work for PROJ-003. Recommend updating PROJ-003 with the two Field Lab findings (F3 cost baseline, F5 deliverable trail) and a 16 May fact-check note so the audit response is tracked end-to-end.', impact: ['project · PROJ-003', 'sched · 16 May'],
        mutations: { projectNote: { field: 'statusDetail', text: 'GAO-26-308 draft findings received 13 May. 2 findings touch Field Lab: F3 cost-baseline docs (RAVEn/LIMS), F5 deliverable acceptance trail (ITPMS). Fact-check corrections due 16 May COB.' }, correspondence: { id: 'CORR-GAO-A', channel: 'Outlook Email', to: 'K. Park (GAO Liaison)', subject: 'RE: GAO-26-308 · fact-check in progress', body: 'Ms. Park — acknowledging receipt of the draft findings. We are reviewing Findings 3 and 5 for factual accuracy and will return marked corrections with documentation references by 16 May COB.' } } },
      { letter: 'B', title: 'Log audit-exposure risk · deliverable acceptance trail (F5)', detail: 'Finding 5 (gaps in the contractor deliverable acceptance audit trail) is a live risk. Log it so the corrective action is tracked. Note: the deliverable acceptance trail is exactly what Stella\u2019s history + correspondence log addresses — strong rebuttal evidence.', impact: ['risk · new', 'project · PROJ-003'],
        mutations: { newRisk: { id: 'R-GAO-F5', title: 'GAO-26-308 Finding 5 · contractor deliverable acceptance audit-trail gaps', score: 9, level: 'Medium', trend: 'flat', owner: 'Steve Palmer', category: 'Governance' } } },
      { letter: 'C', title: 'File Jira + task · fact-check corrections due 16 May COB', detail: 'File a Jira task and a tracked My-Task to assemble factual corrections for Findings 3 and 5 with documentation references, due 16 May COB. Owner: Steve Palmer (C. Mendoza supporting on LIMS cost-baseline evidence).', impact: ['jira · new', 'task · new'],
        mutations: { newJira: { id: 'ITPMS-222', summary: 'GAO-26-308 fact-check · corrections + doc refs for Findings 3 & 5 (cost baseline, deliverable trail)', type: 'Task', priority: 'Highest', status: 'To Do', assignee: 'SP', points: 5 }, newTask: { id: 'TK-GAO-01', title: 'GAO-26-308 fact-check · corrections + doc refs for Findings 3 & 5', due: '16 May COB', priority: 'Highest' } } },
    ],
  },

  cellebrite: {
    targetProject: 'PROJ-002', modalTitle: 'Stella Email Triage · Cellebrite 8-seat expansion',
    flagged: '13 May 2026 · Mon · 1031 EDT', from: 'D. Vargas, Lab IT Manager <d.vargas@ice.dhs.gov>',
    subject: 'Cellebrite license expansion — request for 8 add\u2019l seats for Op STELLAR HORIZON',
    bodyHtml: 'Steve,<br><br>We\u2019re running thin on Premium licenses. The cyber group is sharing 4 across 11 examiners and Op STELLAR HORIZON just added 8 more analysts who need access. We need <strong>8 additional seats</strong> to keep the surge moving.<br><br>The ITAR package is mostly ready — market research and BWS done, Privacy checklist in the FedSpace approval queue. Can you push the federal sign-off? If we don\u2019t have seats by end of month the surge analysts are blocked.<br><br>Thanks,<br>Diana',
    detected: [
      { type: 'roadblock', ico: ROADBLOCK_ICO, label: 'Blocker', desc: '8 surge analysts blocked without seats' },
      { type: 'schedule', ico: '⧉', label: 'Deadline', desc: 'Seats needed by end of month' },
      { type: 'finance', ico: '◈', label: 'Procurement', desc: 'ITAR package awaiting federal sign-off' },
      { type: 'risk', ico: RISK_ICO, label: 'Capacity', desc: '4 Premium licenses shared across 11 examiners' },
    ],
    recs: [
      { letter: 'A', title: 'Advance PROJ-002 · mark ITAR package ready for sign-off', detail: 'The market research, BWS, and Privacy checklist are complete; the ITAR package is in the FedSpace approval queue. Update PROJ-002 to "ready for federal sign-off" and flag the end-of-month deadline driven by the surge.', impact: ['project · PROJ-002', 'sched · EOM'],
        mutations: { projectNote: { field: 'statusLabel', text: 'ITAR package ready · awaiting federal sign-off (EOM)' }, correspondence: { id: 'CORR-CELL-A', channel: 'Outlook Email', to: 'J. Hartwell (COR)', subject: 'Cellebrite ITAR · ready for federal sign-off · EOM deadline', body: 'Jim — the 8-seat Cellebrite ITAR package is complete (market research, BWS, Privacy). In the FedSpace approval queue. Surge (Op STELLAR HORIZON) needs seats by EOM or 8 analysts are blocked. Requesting expedited federal sign-off.' } } },
      { letter: 'B', title: 'Log capacity risk · license shortfall blocking surge', detail: '4 Premium licenses shared across 11 examiners plus 8 new surge analysts is a capacity shortfall. Log a Medium risk so the blocker is visible until seats are provisioned.', impact: ['risk · new', 'project · PROJ-002'],
        mutations: { newRisk: { id: 'R-CELL-CAP', title: 'Cellebrite Premium license shortfall blocking Op STELLAR HORIZON surge (8 analysts)', score: 9, level: 'Medium', trend: 'up', owner: 'D. Vargas', category: 'Capacity' } } },
      { letter: 'C', title: 'File Jira · expedite federal sign-off · 8 seats', detail: 'File a High-priority Jira task to expedite the federal sign-off on the Cellebrite ITAR package, due before end of month. Owner: B. Liu, coordinating with J. Hartwell (COR).', impact: ['jira · new', 'sched · EOM'],
        mutations: { newJira: { id: 'ITPMS-223', summary: 'Expedite Cellebrite ITAR federal sign-off · 8 seats · Op STELLAR HORIZON · before EOM', type: 'Task', priority: 'High', status: 'To Do', assignee: 'BL', points: 3 } } },
    ],
  },

  datacall: {
    targetProject: 'PROJ-006', modalTitle: 'Stella Email Triage · FY26 IT Spend data call',
    flagged: '13 May 2026 · Mon · 0615 EDT', from: 'DHS HQ — Tasker Mgmt System <tasker@hq.dhs.gov>',
    subject: 'DATA CALL: FY26 IT Spend by Program — HSI Components (Due 09 May)',
    bodyHtml: 'TASKER — ACTION REQUIRED.<br><br>The DHS CIO is requesting consolidated IT spend by L2/L3 investment across all HSI components. Steve, your branch is on the distribution. Please return the spend roll-up for your 11 programs across 6 customer areas by <strong>09 May COB</strong>.<br><br>Use the standard SF-template. Late submissions are reported to the component CIO. Reference: TASKER-2026-0512.',
    detected: [
      { type: 'schedule', ico: '⧉', label: 'Hard Deadline', desc: 'Spend roll-up due 09 May COB' },
      { type: 'roadblock', ico: ROADBLOCK_ICO, label: 'Escalation', desc: 'Late → reported to component CIO' },
      { type: 'personnel', ico: PEOPLE_ICO, label: 'Assignment', desc: '11 programs / 6 customer areas to consolidate' },
      { type: 'finance', ico: '◈', label: 'Data Call', desc: 'L2/L3 investment spend by program' },
    ],
    recs: [
      { letter: 'A', title: 'File Jira · FY26 IT spend roll-up · owner D. Reyes', detail: 'File a High-priority Jira task to compile the FY26 IT spend roll-up across all 11 programs / 6 customer areas using the standard SF-template, due 09 May COB. Owner: D. Reyes (program-finance analyst).', impact: ['jira · new', 'person · D. Reyes'],
        mutations: { newJira: { id: 'ITPMS-224', summary: 'FY26 IT spend roll-up · 11 programs / 6 customer areas · DHS CIO data call TASKER-2026-0512', type: 'Task', priority: 'High', status: 'To Do', assignee: 'DR', points: 5 }, newTask: { id: 'TK-DC-01', title: 'FY26 IT spend roll-up for DHS CIO data call · due 09 May COB', due: '09 May COB', priority: 'High' } } },
      { letter: 'B', title: 'Acknowledge tasker · confirm receipt to HQ', detail: 'Send a receipt acknowledgement to the Tasker Mgmt System referencing TASKER-2026-0512 with the committed return date so the branch shows responsive on the HQ tracker.', impact: ['corr · new'],
        mutations: { correspondence: { id: 'CORR-DC-A', channel: 'Outlook Email', to: 'DHS HQ Tasker Mgmt System', subject: 'RE: TASKER-2026-0512 · FY26 IT Spend data call · acknowledged', body: 'Acknowledging TASKER-2026-0512. The Field Lab branch will return the consolidated FY26 IT spend roll-up (11 programs, 6 customer areas) on the standard SF-template by 09 May COB. POC: D. Reyes.' } } },
    ],
  },

  exhibit300: {
    targetProject: 'PROJ-001', modalTitle: 'Stella Email Triage · Exhibit 300 semi-annual',
    flagged: '13 May 2026 · Mon · 0900 EDT', from: 'OMB Budget Liaison — A. Chen <a.chen@omb.eop.gov>',
    subject: 'Exhibit 300 — Semi-annual due 06 JUN 26 (HSI investments)',
    bodyHtml: 'Reminder: the <strong>LIMS modernization and IDEAL 2.0 Exhibit 300</strong> packages are due to OMB by <strong>06 Jun</strong>. The Statement of Funds (SOF) and IT Dashboard cost/schedule/performance data must reconcile to your latest baselines.<br><br>Two areas to watch from last cycle: the cost-baseline narrative (flagged by GAO) and the operational-analysis section. Please confirm your POC and start date.<br><br>A. Chen, OMB',
    detected: [
      { type: 'schedule', ico: '⧉', label: 'Deadline', desc: 'Exhibit 300 packages due 06 Jun' },
      { type: 'risk', ico: RISK_ICO, label: 'Reconciliation', desc: 'SOF + IT Dashboard must match baselines' },
      { type: 'roadblock', ico: ROADBLOCK_ICO, label: 'GAO Overlap', desc: 'Cost-baseline narrative flagged by GAO' },
      { type: 'finance', ico: '◈', label: 'Budget Doc', desc: 'LIMS + IDEAL 2.0 investments' },
    ],
    recs: [
      { letter: 'A', title: 'File Jira · Exhibit 300 packages (LIMS + IDEAL 2.0)', detail: 'File a Jira task to assemble the two Exhibit 300 packages, reconciling SOF and IT Dashboard data to current baselines, due 06 Jun. Owner: D. Reyes. Flag the cost-baseline narrative overlap with GAO Finding 3.', impact: ['jira · new', 'sched · 06 Jun'],
        mutations: { newJira: { id: 'ITPMS-225', summary: 'Exhibit 300 semi-annual · LIMS + IDEAL 2.0 · reconcile SOF/IT-Dashboard to baselines · due 06 Jun', type: 'Task', priority: 'High', status: 'To Do', assignee: 'DR', points: 8 }, newTask: { id: 'TK-EX3-01', title: 'Exhibit 300 packages (LIMS + IDEAL 2.0) · due 06 Jun OMB', due: '06 Jun', priority: 'High' } } },
      { letter: 'B', title: 'Link to GAO cost-baseline risk · narrative consistency', detail: 'The Exhibit 300 cost-baseline narrative must stay consistent with the GAO-26-308 fact-check (Finding 3, RAVEn/LIMS). Log a note so the two work streams reconcile rather than contradict each other.', impact: ['project · PROJ-001', 'risk · consistency'],
        mutations: { projectNote: { field: 'statusDetail', text: 'Exhibit 300 (LIMS) cost-baseline narrative must reconcile with GAO-26-308 Finding 3 fact-check. Coordinate D. Reyes (OMB) + Steve (GAO) to keep narratives consistent.' } } },
    ],
  },

  qbr: {
    targetProject: 'PROJ-004', modalTitle: 'Stella Email Triage · QBR read-ahead',
    flagged: '13 May 2026 · Mon · 0930 EDT', from: 'HSIA Director — M. Okonkwo <m.okonkwo@ice.dhs.gov>',
    subject: 'QBR read-ahead due Wed 1800 — please send your ITPMS enterprise roll-up',
    bodyHtml: 'Steve,<br><br>Need your one-pager (<strong>ITPMS enterprise roll-up · all 6 customer areas</strong>) by <strong>COB Wednesday</strong>. I\u2019ll consolidate with NER and SWR for the Director\u2019s QBR read-ahead.<br><br>Keep it to a single page: status by customer area, top 3 risks, top 3 wins, and any asks. Highlight anything that needs the Director\u2019s air cover.<br><br>Thanks,<br>Marcus',
    detected: [
      { type: 'schedule', ico: '⧉', label: 'Deadline', desc: 'One-pager due Wed 1800' },
      { type: 'personnel', ico: PEOPLE_ICO, label: 'Assignment', desc: '6 customer areas to roll up' },
      { type: 'finance', ico: '◈', label: 'Executive', desc: 'Feeds Director QBR read-ahead' },
      { type: 'risk', ico: RISK_ICO, label: 'Visibility', desc: 'Top-3 risks need Director air cover' },
    ],
    recs: [
      { letter: 'A', title: 'File Jira · ITPMS enterprise one-pager · owner D. Reyes', detail: 'File a Jira task to assemble the single-page ITPMS enterprise roll-up (status by customer area, top-3 risks, top-3 wins, asks), due Wed 1800. Owner: D. Reyes, review with Steve.', impact: ['jira · new', 'sched · Wed 1800'],
        mutations: { newJira: { id: 'ITPMS-226', summary: 'ITPMS enterprise QBR one-pager · 6 customer areas · status/risks/wins/asks · due Wed 1800', type: 'Task', priority: 'High', status: 'To Do', assignee: 'DR', points: 3 }, newTask: { id: 'TK-QBR-01', title: 'ITPMS enterprise QBR one-pager for M. Okonkwo · due Wed 1800', due: 'Wed 1800', priority: 'High' } } },
      { letter: 'B', title: 'Acknowledge to M. Okonkwo · confirm delivery', detail: 'Send a short acknowledgement confirming the one-pager will arrive by Wed 1800 and noting the BWC SLA breach as a likely "needs air cover" item for the Director.', impact: ['corr · new'],
        mutations: { correspondence: { id: 'CORR-QBR-A', channel: 'Teams', to: 'M. Okonkwo (HSIA Director)', subject: 'QBR one-pager · confirmed for Wed 1800', body: 'Marcus — confirmed, the ITPMS enterprise one-pager (6 customer areas) will be in your inbox by Wed 1800. Flagging now: the ERO ATD Portal SLA breaches are a likely "needs air cover" item for the Director.' } } },
    ],
  },
};

// which emails have been triaged this session / undo log
const TRIAGE_DONE: Record<string, Record<string, boolean>> = {};
const TRIAGE_UNDO: Record<string, UndoRecord> = {};

function _triageProjName(id: string): string {
  const proj = _proj(id);
  return proj ? proj.name : id;
}

function _triageRecPills(rec: Recommendation, targetProject: string): string[] {
  const m = rec.mutations || {}; const emb: EmbeddedItem[] = [];
  if (m.newJira) emb.push({ kind: 'jira', id: m.newJira.id });
  if (m.newTask) emb.push({ kind: 'task', id: m.newTask.id });
  if (m.newRisk) emb.push({ kind: 'risk', id: m.newRisk.id });
  if (m.correspondence) emb.push({ kind: 'correspondence', id: m.correspondence.id });
  const pills = emb.map((it) => _embedPillHtml(it));
  if (m.projectNote) pills.push(`<span class="embed-pill project" ${_tc('triageOpenProject', targetProject)}><span class="embed-pill-ico">▤</span>${targetProject}</span>`);
  return pills;
}

// ── the data-driven triage modal (index.html:20850 triageEmail) ──────────────
function triageEmail(emailId: string): void {
  const e = TRIAGE_EMAILS[emailId];
  if (!e) { if (emailId === 'raven') { projectsTriageDemo(); return; } window.alert('No triage scenario for this email.'); return; }
  const projName = _triageProjName(e.targetProject || '');
  // No targetProject = the model matched this email to none of the candidate
  // projects. Say so rather than naming one; applying is blocked below.
  const routeLabel = e.targetProject
    ? 'routes to ' + e.targetProject + ' ' + projName
    : 'no matching project — recommendations are unrouted';

  const emailHtml = `<div class="triage-email">
    <div class="triage-email-h">${RISK_ICO} Flagged · Incoming · ${e.flagged}</div>
    <div class="triage-email-from">From: ${e.from.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>
    <div class="triage-email-subj">Subject: ${e.subject}</div>
    <div class="triage-email-body">${e.bodyHtml}</div>
  </div>`;

  const chipsHtml = e.detected.map((d) => `<div class="triage-detected-chip ${d.type}">
    <span class="triage-detected-chip-ico">${d.ico}</span>
    <div><div class="triage-detected-chip-l">${pbEsc(d.label)}</div><div class="triage-detected-chip-d">${pbEsc(d.desc)}</div></div>
  </div>`).join('');

  const aiBanner = `<div class="triage-ai-banner-v24">
    <div class="triage-ai-banner-v24-h">
      <div class="triage-ai-banner-v24-ico">${SPARK_SVG}</div>
      <div class="triage-ai-banner-v24-h-text">
        <div class="triage-ai-banner-v24-eyebrow">Stella · Auto-Triage Complete · ${routeLabel}</div>
        <div class="triage-ai-banner-v24-t">${e.recs.length} Recommendations Generated</div>
      </div>
      <div class="triage-ai-banner-v24-meta"><div class="triage-ai-banner-v24-meta-v">~2s</div><div class="triage-ai-banner-v24-meta-l">Stella processing</div></div>
    </div>
    <div class="triage-detected"><div class="triage-detected-h">⧉ Detected in this email</div><div class="triage-detected-chips">${chipsHtml}</div></div>
  </div>`;

  const recsHtml = e.recs.map((r) => {
    const applied = TRIAGE_DONE[emailId] && TRIAGE_DONE[emailId][r.letter];
    // Prefills ServiceNowTicketModal (InboxPage.tsx) via the window.openServiceNowTicket
    // bridge — see the _tc/data-tc click-delegation note above this function.
    // title carries the recommendation's own title, not just the parent
    // email's subject — every recommendation for the same email otherwise
    // seeds ServiceNowTicketModal's ticketNumber() (hashed from title+
    // createdAt) identically, producing indistinguishable tickets for
    // different action items. Found via /verify 2026-08-13.
    const snPayload = JSON.stringify({
      title: e.subject ? `${r.title} — ${e.subject}` : r.title,
      isResolved: false,
      caller: e.from,
      openedBy: 'Atlas Email Triage',
      category: 'Email Triage',
      impact: e.flagged ? '2 - Medium' : '3 - Low',
      urgency: e.flagged ? '2 - Medium' : '3 - Low',
      priority: e.flagged ? '2 - High' : '4 - Low',
      contactType: 'Email',
      assignmentGroup: e.targetProject ? `${e.targetProject} team` : 'Service Desk',
      workNotes: r.detail.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 400),
      changedFields: 'Category, Impact, Urgency, Priority',
    });
    return `<div class="triage-rec${applied ? ' applied' : ''}" id="triage-rec-${r.letter}">
    <div class="triage-rec-h">
      <div style="display:flex;align-items:center;flex:1"><span class="triage-rec-letter">${r.letter}</span><div class="triage-rec-title">${pbEsc(r.title)}</div></div>
      <span class="triage-rec-label" id="triage-rec-${r.letter}-label">${applied ? 'Applied ' + CHECK_SVG : 'Recommended'}</span>
    </div>
    <div class="triage-rec-detail">${r.detail}</div>
    <div class="triage-rec-impact">${r.impact.map((p) => {
      const cls = p.startsWith('risk') ? 'risk' : p.startsWith('sched') ? 'sched' : p.startsWith('person') ? 'person' : p.startsWith('task') ? 'task' : p.startsWith('jira') ? 'task' : p.startsWith('project') ? 'sched' : '';
      return `<span class="triage-rec-impact-pill ${cls}">${p}</span>`;
    }).join('')}</div>
    ${applied ? `<div class="triage-rec-created"><span class="triage-rec-created-l">${CHECK_SVG} Created / updated — click to open:</span>${_triageRecPills(r, e.targetProject || '').join('')}</div>` : ''}
    <div class="triage-rec-foot">
      ${applied
        ? `<button class="triage-rec-btn skip" ${_tc('triageUndoRec', emailId, r.letter)}>↩ Undo</button><button class="triage-rec-btn" id="triage-rec-${r.letter}-btn" disabled>${CHECK_SVG} Applied</button>`
        : `<button class="triage-rec-btn skip" ${_tc('triageSkipRec', r.letter)}>Skip</button><button class="triage-rec-btn" id="triage-rec-${r.letter}-btn" ${_tc('triageApplyRec', emailId, r.letter)}>${CHECK_SVG} Apply recommendation</button>`}
      <button class="triage-rec-btn sn" ${_tc('openServiceNowTicket', snPayload)}>⚡ Create ServiceNow ticket</button>
    </div>
  </div>`;
  }).join('');

  const body = `<div class="triage-modal">
    ${emailHtml}
    ${aiBanner}
    <div style="font-family:var(--ff);font-size:13px;font-weight:700;color:var(--navy);margin-bottom:9px;letter-spacing:-.005em">Recommended actions · ${e.targetProject
      ? `routes to <span style="color:var(--gold)">${e.targetProject} · ${projName}</span> · created artifacts populate Jira, Tasks, Risks, and My Workspace`
      : `<span style="color:var(--red)">no matching project</span> — Stella did not match this email to any of your projects, so artifacts land unrouted in My Workspace instead.`}</div>
    <div class="triage-recs">${recsHtml}</div>
  </div>`;

  const primaryFooter = `<button class="btn btn--primary btn--sm" ${_tc('triageApplyAll', emailId)}>${CHECK_SVG} Apply all recommendations</button>`;
  _openXlModal(e.modalTitle, body, `<button class="btn btn--sm" ${_tc('closeMo')}>Close</button>${primaryFooter}`);
  _activity({ status: 'info', label: 'Email Triage · Stella recommendations generated', detail: e.recs.length + ' recommendations · ' + e.subject.substring(0, 46) + ' · ' + routeLabel, dur: 'Stella · ~2s' });
}

// dim a recommendation card and mark it skipped, without applying it
function triageSkipRec(letter: string): void {
  const el = document.getElementById('triage-rec-' + letter);
  if (el) el.style.opacity = '0.4';
  const lbl = document.getElementById('triage-rec-' + letter + '-label');
  if (lbl) lbl.textContent = 'Skipped';
}

// ── Apply one recommendation (index.html:20975 triageApplyRec) ───────────────
function triageApplyRec(emailId: string, letter: string): void {
  const e = TRIAGE_EMAILS[emailId]; if (!e) return;
  const rec = e.recs.find((r) => r.letter === letter); if (!rec) return;
  // ATLAS has no "My Projects" store (window.PROJECTS is never populated —
  // that feature was never ported), so the model can never return a
  // targetProject and _proj() always returns null. This used to hard-block
  // the whole apply behind an alert whenever either was missing; the
  // artifacts below (task/jira/risk) land on My Workspace regardless — only
  // the project-linked bookkeeping (history, related-items, field patches)
  // is skipped when there's no project to attach it to.
  const routeLabel = e.targetProject || 'unrouted';
  const proj = e.targetProject ? _proj(e.targetProject) : null;
  const today = demoStamp();
  const m = rec.mutations || {};
  const historyParts: string[] = []; const embedded: EmbeddedItem[] = [];
  const undo: UndoRecord = { tasks: [], risks: [], jira: [], corr: [], projField: null, projFieldOld: null, projId: e.targetProject };
  const rel = proj ? _cloneRelated(proj.relatedItems) : null;
  const fieldPatch: Record<string, any> = {};

  if (m.newJira) {
    _injectJira(m.newJira);
    _fileLiveJira(m.newJira, 'inbox rec ' + letter);
    if (rel) { if (!rel.jira) rel.jira = []; if (!rel.jira.includes(m.newJira.id)) rel.jira.push(m.newJira.id); }
    historyParts.push('Jira <strong>' + m.newJira.id + '</strong> filed: <em>"' + m.newJira.summary + '"</em> (' + m.newJira.type + ', ' + m.newJira.priority + ').');
    embedded.push({ kind: 'jira', id: m.newJira.id }); undo.jira.push(m.newJira.id);
  }
  if (m.newTask) {
    _injectTask(m.newTask, 'Stella Triage · ' + routeLabel, { date: today, recommendation: 'Stella Triage Rec ' + letter, summary: m.newTask.title, outcome: 'Task filed to My Workspace. Linked to ' + routeLabel + '.', source: 'Stella Triage · ' + routeLabel });
    if (rel && rel.tasks && !rel.tasks.includes(m.newTask.id)) rel.tasks.push(m.newTask.id);
    historyParts.push('Task <strong>' + m.newTask.id + '</strong> filed: <em>"' + m.newTask.title + '"</em> · due ' + m.newTask.due + '.');
    embedded.push({ kind: 'task', id: m.newTask.id }); undo.tasks.push(m.newTask.id);
  }
  if (m.newRisk) {
    _injectRisk(m.newRisk, { date: today, recommendation: 'Stella Triage Rec ' + letter, summary: m.newRisk.title, outcome: 'Risk filed to register (' + m.newRisk.level + ', score ' + m.newRisk.score + '). Linked to ' + routeLabel + '.', source: 'Stella Triage · ' + routeLabel });
    if (rel) { if (!rel.risks) rel.risks = []; if (!rel.risks.includes(m.newRisk.id)) rel.risks.push(m.newRisk.id); }
    historyParts.push('Risk <strong>' + m.newRisk.id + '</strong> logged: ' + m.newRisk.title + ' <span class="pill pill-new pill-risk">' + m.newRisk.score + '</span>.');
    embedded.push({ kind: 'risk', id: m.newRisk.id }); undo.risks.push(m.newRisk.id);
  }
  if (m.projectNote && proj) {
    const f = m.projectNote.field; undo.projField = f; undo.projFieldOld = proj[f];
    fieldPatch[f] = m.projectNote.text;
    historyParts.push('Project <strong>' + f + '</strong> updated from Stella triage.');
  }
  if (m.correspondence) {
    _injectCorrespondence(m.correspondence, letter, e.targetProject || '', today);
    if (rel) { if (!rel.correspondence) rel.correspondence = []; if (!rel.correspondence.includes(m.correspondence.id)) rel.correspondence.push(m.correspondence.id); }
    embedded.push({ kind: 'correspondence', id: m.correspondence.id }); undo.corr.push(m.correspondence.id);
  }

  if (proj) {
    const histEntry: HistoryEntry = { date: today, type: 'ai-recommendation', actor: 'Stella', actorRole: 'Auto-triage · ' + emailId, content: historyParts.join(' '), source: 'Stella Triage · auto', sourceIcon: SPARK_SVG, embedded: embedded.length ? embedded : null, _triageKey: emailId + ':' + letter };
    const newHistory = [histEntry, ...(proj.history || [])];
    _patchProject(e.targetProject, { ...fieldPatch, relatedItems: rel, history: newHistory, _justUpdated: true });
  }

  TRIAGE_UNDO[emailId + ':' + letter] = undo;
  if (!TRIAGE_DONE[emailId]) TRIAGE_DONE[emailId] = {};
  TRIAGE_DONE[emailId][letter] = true;

  _markRecApplied(letter, embedded, m.projectNote ? e.targetProject : null, emailId);
  triageMarkInboxState(emailId);
  triageUpdateBadges();
  _activity({ status: 'ok', label: 'Stella Triage · ' + emailId + ' rec ' + letter + ' applied', detail: historyParts.join(' ').replace(/<[^>]+>/g, '').substring(0, 90), dur: 'manual · <1s' });
  // (UX audit #2) monolith globals window.projectsSave / window.aiRenderProjectsBoard
  // were never ported — the guarded calls silently no-opped. The store patch above
  // already re-renders the board, and the React port has no PROJECTS persistence layer.
}

// modal UI: swap Apply→Applied, Skip→Undo, render created-item pills
function _markRecApplied(letter: string, embedded: EmbeddedItem[], projectNoteId: string | null, emailId: string): void {
  const recEl = document.getElementById('triage-rec-' + letter);
  if (!recEl) return;
  recEl.classList.add('applied');
  const btn = document.getElementById('triage-rec-' + letter + '-btn') as HTMLButtonElement | null;
  if (btn) { btn.innerHTML = CHECK_SVG + ' Applied'; btn.disabled = true; }
  const lbl = document.getElementById('triage-rec-' + letter + '-label'); if (lbl) lbl.innerHTML = 'Applied ' + CHECK_SVG;
  const skip = recEl.querySelector('.triage-rec-btn.skip');
  if (skip) { (skip as HTMLElement).textContent = '↩ Undo'; _setTc(skip, 'triageUndoRec', emailId, letter); }
  const pills = (embedded || []).map((it) => _embedPillHtml(it));
  if (projectNoteId) pills.push(`<span class="embed-pill project" ${_tc('triageOpenProject', projectNoteId)}><span class="embed-pill-ico">▤</span>${projectNoteId}</span>`);
  if (pills.length) {
    let row = recEl.querySelector('.triage-rec-created');
    if (!row) { row = document.createElement('div'); row.className = 'triage-rec-created'; const foot = recEl.querySelector('.triage-rec-foot'); recEl.insertBefore(row, foot); }
    row.innerHTML = '<span class="triage-rec-created-l">' + CHECK_SVG + ' Created / updated — click to open:</span>' + pills.join('');
  }
}

// ── Undo one recommendation (index.html:21060 triageUndoRec) ─────────────────
function triageUndoRec(emailId: string, letter: string): void {
  const key = emailId + ':' + letter; const u = TRIAGE_UNDO[key]; if (!u) return;
  const proj = _proj(u.projId);
  u.jira.forEach((id) => _removeJira(id));
  u.tasks.forEach((id) => _removeTask(id));
  u.risks.forEach((id) => _removeRisk(id));
  u.corr.forEach((id) => _removeCorrespondence(id));
  if (proj) {
    const patch: Record<string, any> = {};
    if (u.projField) patch[u.projField] = u.projFieldOld;
    const rel = _cloneRelated(proj.relatedItems);
    if (rel) {
      ['jira', 'tasks', 'risks', 'correspondence'].forEach((k) => {
        if (rel[k]) rel[k] = rel[k]!.filter((x) => !u.jira.includes(x) && !u.tasks.includes(x) && !u.risks.includes(x) && !u.corr.includes(x));
      });
      patch.relatedItems = rel;
    }
    if (proj.history) patch.history = proj.history.filter((h) => h._triageKey !== key);
    _patchProject(u.projId, patch);
  }
  delete TRIAGE_UNDO[key];
  if (TRIAGE_DONE[emailId]) delete TRIAGE_DONE[emailId][letter];

  const recEl = document.getElementById('triage-rec-' + letter);
  if (recEl) {
    recEl.classList.remove('applied'); recEl.style.opacity = '1';
    const btn = document.getElementById('triage-rec-' + letter + '-btn') as HTMLButtonElement | null;
    if (btn) { btn.innerHTML = CHECK_SVG + ' Apply recommendation'; btn.disabled = false; _setTc(btn, 'triageApplyRec', emailId, letter); }
    const lbl = document.getElementById('triage-rec-' + letter + '-label'); if (lbl) lbl.textContent = 'Recommended';
    const skip = recEl.querySelector('.triage-rec-btn.skip');
    if (skip) { (skip as HTMLElement).textContent = 'Skip'; _setTc(skip, 'triageSkipRec', letter); }
    const row = recEl.querySelector('.triage-rec-created'); if (row) row.remove();
  }
  triageMarkInboxState(emailId);
  triageUpdateBadges();
  _activity({ status: 'info', label: 'Stella Triage · rec ' + letter + ' undone', detail: 'Reverted artifacts created for ' + emailId + ' rec ' + letter, dur: 'manual · <1s' });
  // (UX audit #2) monolith globals window.projectsSave / window.aiRenderProjectsBoard
  // were never ported — the guarded calls silently no-opped. The store patch above
  // already re-renders the board, and the React port has no PROJECTS persistence layer.
}

// The created-item pills used to call window.oralsOpenGeneratedItem, which
// OralsPage installs on mount and deletes on unmount — so from the inbox, where
// Task Master is not the mounted page, "click to open" silently did nothing.
// This looks the item up in the same window.__WS_* arrays My Workspace's
// picker reads (_wsArray, above) and renders a real detail modal in place —
// no navigation needed, which also avoids losing the in-memory item on a
// route change before the user ever sees it.
const _WS_KIND_KEY: Record<string, string> = { jira: '__WS_JIRA', task: '__WS_TASKS', risk: '__WS_RISKS', correspondence: '__WS_CORRESPONDENCE' };

function _itemNotFoundModal(id: string): void {
  _openXlModal(BOLT_SVG + ' ' + pbEsc(id), '<p style="font-family:var(--ff);font-size:13px;color:var(--t2);margin:0">Couldn’t find this item — it may have been undone.</p>', `<button class="btn btn--sm" ${_tc('closeMo')}>Close</button>`);
}

function _appliedActionsHtml(item: any): string {
  const actions = item && Array.isArray(item.appliedActions) ? item.appliedActions : [];
  if (!actions.length) return '';
  const rows = actions.map((a: any) => `
    <div class="triage-rec-created" style="margin-left:0;margin-top:8px">
      <div style="font-weight:600">${pbEsc(a.date || '')} · ${pbEsc(a.recommendation || '')}</div>
      <div style="color:var(--t2);margin-top:2px">${pbEsc(a.outcome || a.summary || '')}</div>
    </div>`).join('');
  return `<div style="margin-top:16px"><div class="triage-detected-h">Activity</div>${rows}</div>`;
}

function openGeneratedItemModal(kind: string, id: string): void {
  const key = _WS_KIND_KEY[kind];
  const item = key ? _wsArray(key).find((x) => x && x.id === id) : null;
  if (!item) { _itemNotFoundModal(id); return; }

  let fields = '';
  if (kind === 'task') {
    fields = `<div class="triage-detected-h">Due</div><p>${pbEsc(item.due || '—')}</p><div class="triage-detected-h">Priority</div><p>${pbEsc(item.priority || '—')}</p>`;
  } else if (kind === 'jira') {
    fields = `<div class="triage-detected-h">Type</div><p>${pbEsc(item.type || '—')}</p><div class="triage-detected-h">Priority</div><p>${pbEsc(item.priority || '—')}</p><div class="triage-detected-h">Status</div><p>${pbEsc(item.status || '—')}</p><div class="triage-detected-h">Assignee</div><p>${pbEsc(item.assignee || '—')}</p>`;
  } else if (kind === 'risk') {
    fields = `<div class="triage-detected-h">Score</div><p>${pbEsc(item.score ?? '—')} · ${pbEsc(item.level || '')}</p><div class="triage-detected-h">Trend</div><p>${pbEsc(item.trend || '—')}</p><div class="triage-detected-h">Owner</div><p>${pbEsc(item.owner || '—')}</p>`;
  } else if (kind === 'correspondence') {
    fields = `<div class="triage-detected-h">Channel</div><p>${pbEsc(item.channel || '—')} · to ${pbEsc(item.to || '—')}</p><div class="triage-detected-h">Subject</div><p>${pbEsc(item.subject || '—')}</p><div class="triage-detected-h">Body</div><p>${pbEsc(item.body || '—')}</p>`;
  }

  const title = item.title || item.summary || item.subject || id;
  const body = `<p style="font-family:var(--ff);font-size:12px;color:var(--t2);margin:0 0 12px">${pbEsc(id)}</p>${fields}${_appliedActionsHtml(item)}`;
  const footer = `<button class="btn btn--sm" ${_tc('closeMo')}>Close</button><button class="btn btn--primary btn--sm" ${_tc('pbGoToWorkspace')}>Open in My Workspace</button>`;
  _openXlModal(pbEsc(title), body, footer);
}

function triageOpenItem(kind: string, id: string): void {
  openGeneratedItemModal(kind, id);
}

// ATLAS never got a "My Projects" drill-down page ported from dev_projectbuilder
// (window.PROJECTS is always empty — see _proj() above), so there is no real
// destination to send this to. Rather than silently no-op like the old
// window.showPage/projectDrillDown calls (neither exists in Atlas), say so.
function triageOpenProject(projId: string): void {
  _openXlModal(BOLT_SVG + ' ' + pbEsc(projId), '<p style="font-family:var(--ff);font-size:13px;color:var(--t2);margin:0">Project drill-down isn’t available in Atlas yet — this reference is tracked on the email’s recommendation only.</p>', `<button class="btn btn--sm" ${_tc('closeMo')}>Close</button>`);
}

function triageApplyAll(emailId: string): void {
  const e = TRIAGE_EMAILS[emailId]; if (!e) return;
  e.recs.forEach((r) => { const btn = document.getElementById('triage-rec-' + r.letter + '-btn') as HTMLButtonElement | null; if (btn && !btn.disabled) triageApplyRec(emailId, r.letter); });
}

// ── inbox triaged-state + badges (index.html:21112-21146) ────────────────────
function _triageEmailFullyDone(emailId: string): boolean {
  const e = TRIAGE_EMAILS[emailId]; if (!e) return false;
  const done = TRIAGE_DONE[emailId]; if (!done) return false;
  return e.recs.every((r) => done[r.letter]);
}
function triageMarkInboxState(emailId: string): void {
  const btn = document.querySelector('.mail-triage-btn[data-triage="' + emailId + '"]');
  if (!btn) return;
  const anyDone = TRIAGE_DONE[emailId] && Object.values(TRIAGE_DONE[emailId]).some(Boolean);
  if (_triageEmailFullyDone(emailId)) {
    btn.innerHTML = CHECK_SVG + ' Triaged'; btn.classList.add('done');
  } else if (anyDone) {
    btn.textContent = '◐ Partly triaged'; btn.classList.remove('done');
  } else {
    btn.innerHTML = BOLT_SVG + ' Triage with Stella →'; btn.classList.remove('done');
  }
}
function triageRemainingCount(): number {
  let remaining = 1; // RAVEn demo
  Object.keys(TRIAGE_EMAILS).forEach((id) => { if (!_triageEmailFullyDone(id)) remaining++; });
  return remaining;
}
// Review-queue modal: every TRIAGE_EMAILS entry that isn't fully triaged yet,
// each row opening that email's own triage modal via triageEmail(id) (already
// bridged onto window.triageEmail by InboxPage.tsx's mount effect).
export function triageOpenCenter(): void {
  const remaining = triageRemainingCount();
  const rows = Object.keys(TRIAGE_EMAILS)
    .filter((id) => !_triageEmailFullyDone(id))
    .map((id) => {
      const e = TRIAGE_EMAILS[id];
      return `<div class="triage-center-row" ${_tc('triageEmail', id)} style="cursor:pointer;padding:8px 0;border-bottom:1px solid var(--rule, #E4E7EC)">
        <strong>${e.from}</strong> — ${e.subject}
      </div>`;
    })
    .join('');
  const body = `<div class="triage-center">
    <div class="triage-center-intro"><strong>${remaining}</strong> inbox emails remain to triage.</div>
    ${rows}
  </div>`;
  _openXlModal('Triage Center · inbox ↔ My Projects', body, `<button class="btn btn--sm" ${_tc('closeMo')}>Close</button>`);
}
function triageUpdateBadges(): void {
  const n = triageRemainingCount();
  const badge = document.getElementById('triage-center-count');
  if (badge) badge.textContent = String(n);
  if (typeof (window as any).projectsRender === 'function') { const grid = document.getElementById('proj-grid-view'); if (grid && grid.offsetParent !== null) { try { (window as any).projectsRender(); } catch (_er) { /* no-op */ } } }
}

// ═════════════════════════════════════════════════════════════════════════════
// RAVEn flagship demo (index.html:20277 projectsTriageDemo + 20397 apply funcs)
// ═════════════════════════════════════════════════════════════════════════════
function projectsTriageDemo(): void {
  const emailHtml = `<div class="triage-email">
    <div class="triage-email-h">${RISK_ICO} Flagged · Incoming · 13 May 2026 · 0938 EDT</div>
    <div class="triage-email-from">From: Susan Williamson, Division Chief &lt;swilliamson@ice.dhs.gov&gt;</div>
    <div class="triage-email-subj">Subject: Roadblock — RAVEn ADE-3 stakeholder dependency closure delayed</div>
    <div class="triage-email-body">Steve,<br><br>Quick note before the 1100 meeting. The Office of General Counsel just told me that their reviewer for the RAVEn dependency closure is on family leave until 27 May. That\u2019s 5 days after our scheduled 22 May ADE-3 review. We have a problem.<br><br>I need your team\u2019s recommendation in writing by COB today on: <strong>(a)</strong> can the review proceed on 22 May with OGC concurrence in absentia, <strong>(b)</strong> if we slip the review, what's the minimum slip that preserves the FY26 Q3 funding line, and <strong>(c)</strong> who from your team can pick up the cross-walk OGC was supposed to do so we don't lose more time.<br><br>Also — the RAVEn query latency issue I saw in your stand-up note this morning. Does that affect anything in the ADE-3 package? Please advise.<br><br>Susan</div>
  </div>`;

  const aiBanner = `<div class="triage-ai-banner-v24">
    <div class="triage-ai-banner-v24-h">
      <div class="triage-ai-banner-v24-ico">${SPARK_SVG}</div>
      <div class="triage-ai-banner-v24-h-text">
        <div class="triage-ai-banner-v24-eyebrow">Stella · Auto-Triage Complete</div>
        <div class="triage-ai-banner-v24-t">4 Recommendations Generated</div>
      </div>
      <div class="triage-ai-banner-v24-meta">
        <div class="triage-ai-banner-v24-meta-v">~2s</div>
        <div class="triage-ai-banner-v24-meta-l">Stella processing</div>
      </div>
    </div>
    <div class="triage-detected">
      <div class="triage-detected-h">⧉ Detected in this email</div>
      <div class="triage-detected-chips">
        <div class="triage-detected-chip roadblock">
          <span class="triage-detected-chip-ico">${ROADBLOCK_ICO}</span>
          <div><div class="triage-detected-chip-l">Roadblock</div><div class="triage-detected-chip-d">OGC reviewer unavailable 5 days past ADE-3</div></div>
        </div>
        <div class="triage-detected-chip schedule">
          <span class="triage-detected-chip-ico">⧖</span>
          <div><div class="triage-detected-chip-l">Schedule Impact</div><div class="triage-detected-chip-d">22 May review date threatened</div></div>
        </div>
        <div class="triage-detected-chip risk">
          <span class="triage-detected-chip-ico">${RISK_ICO}</span>
          <div><div class="triage-detected-chip-l">Risk Surface</div><div class="triage-detected-chip-d">R-HSI-007 dependency closure exposure</div></div>
        </div>
        <div class="triage-detected-chip personnel">
          <span class="triage-detected-chip-ico">${PEOPLE_ICO}</span>
          <div><div class="triage-detected-chip-l">Personnel Action</div><div class="triage-detected-chip-d">Cross-walk pickup reassignment needed</div></div>
        </div>
      </div>
    </div>
  </div>`;

  const recs: Array<{ letter: string; title: string; detail: string; impact: string[]; apply: string }> = [
    { letter: 'A', title: 'Update risk R-HSI-007 · escalate score 12 → 15', detail: 'The OGC reviewer unavailability is a new risk surface not previously tracked. Recommend bumping R-HSI-007 from High (12) to Critical (15), updating the risk register with the OGC absence as a new dependency, and changing trend from "down" to "up".', impact: ['risk · R-HSI-007', 'sched · cascade trigger'], apply: 'risk' },
    { letter: 'B', title: 'Update PROJ-001 schedule · ADE-3 from 22 May to 03 Jun', detail: 'OGC reviewer returns 27 May; need at minimum 7 days for cross-walk + IRB packet finalization. Recommend slip to 03 Jun (12 days). This preserves the FY26 Q3 funding line (deadline 15 Jun for funding obligation).', impact: ['sched · +12 days', 'project · PROJ-001'], apply: 'schedule' },
    { letter: 'C', title: 'Reassign D. Reyes from TECS-Mod sprint to OGC cross-walk pickup', detail: 'D. Reyes has prior OGC cross-walk experience from the FY25 TECS-Mod ATO. He can cover OGC review work on RAVEn without OGC being present. Reassign 8 hours/week through 27 May. TECS-Mod sprint absorbs the delta with B. Liu picking up his security review tasks.', impact: ['person · D. Reyes', 'task · TK-006'], apply: 'personnel' },
    { letter: 'D', title: 'Generate task: written response to Susan by COB · covering (a/b/c)', detail: 'Draft response addressing the three asks: (a) Recommend proceeding 03 Jun with OGC concurrence rather than in absentia — cleaner audit trail. (b) Min slip = 12 days. (c) D. Reyes picks up cross-walk. Plus answer the query latency follow-up: hot-path index patch deploying 14 May, no ADE-3 impact projected.', impact: ['task · new', 'person · Steve Palmer'], apply: 'task' },
  ];

  const recsHtml = recs.map((r) => `<div class="triage-rec" id="triage-rec-${r.letter}">
    <div class="triage-rec-h">
      <div style="display:flex;align-items:center;flex:1">
        <span class="triage-rec-letter">${r.letter}</span>
        <div class="triage-rec-title">${pbEsc(r.title)}</div>
      </div>
      <span class="triage-rec-label" id="triage-rec-${r.letter}-label">Recommended</span>
    </div>
    <div class="triage-rec-detail">${r.detail}</div>
    <div class="triage-rec-impact">${r.impact.map((p) => {
      const cls = p.startsWith('risk') ? 'risk' : p.startsWith('sched') ? 'sched' : p.startsWith('person') ? 'person' : p.startsWith('task') ? 'task' : '';
      return `<span class="triage-rec-impact-pill ${cls}">${p}</span>`;
    }).join('')}</div>
    <div class="triage-rec-foot">
      <button class="triage-rec-btn skip" ${_tc('triageSkipRec', r.letter)}>Skip</button>
      <button class="triage-rec-btn" id="triage-rec-${r.letter}-btn" ${_tc('projectsApplyRec', r.letter, r.apply)}>${CHECK_SVG} Apply recommendation</button>
    </div>
  </div>`).join('');

  const body = `<div class="triage-modal">
    ${emailHtml}
    ${aiBanner}
    <div style="font-family:var(--ff);font-size:13px;font-weight:700;color:var(--navy);margin-bottom:9px;letter-spacing:-.005em">Recommended actions · click Apply to propagate changes across the app</div>
    <div class="triage-recs">${recsHtml}</div>
  </div>`;

  _openXlModal('<span class="v56-pa-badge">Power Automate</span> Stella Email Triage · RAVEn ADE-3 roadblock', body, `<button class="btn btn--sm" ${_tc('closeMo')}>Close</button><button class="btn btn--primary btn--sm" ${_tc('projectsApplyAllRecs')}>${CHECK_SVG} Apply all recommendations</button>`);
  _activity({ status: 'info', label: 'Email Triage · Stella recommendations generated', detail: '4 recommendations · RAVEn ADE-3 roadblock · Susan Williamson email', dur: 'Stella · ~2s' });
}

function projectsApplyRec(letter: string, kind: string): void {
  // ATLAS has no "My Projects" store — proj is always null. The task branch
  // below still lands a real item on My Workspace; the risk/schedule/
  // personnel branches are project-linked bookkeeping with nowhere to go.
  const proj = _proj('PROJ-001');
  const today = demoStamp();
  let historyContent = '';
  let correspondence: { id: string; channel: string; to: string; subject: string; body: string } | null = null;
  const embedded: EmbeddedItem[] = [];
  const rel = (proj ? _cloneRelated(proj.relatedItems) : null) || {};
  const fieldPatch: Record<string, any> = {};

  if (kind === 'risk') {
    // R-HSI-007 update — mutated in place in __WS_RISKS (no store update action).
    const risk = ((window as any).__WS_RISKS || []).find((r: any) => r.id === 'R-HSI-007');
    if (risk) {
      risk.score = 15;
      risk.level = 'High';
      risk.trend = 'up';
      risk.title = 'Field Lab ADE-3 stakeholder dependencies open · OGC reviewer unavailable through 27 May';
      if (!risk.appliedActions) risk.appliedActions = [];
      risk.appliedActions.push({ date: today, recommendation: 'Stella Triage Rec A', summary: 'Score escalated 12 → 15. OGC reviewer unavailability added as new dependency. Trend bumped down → up.', outcome: 'Risk register updated. R-HSI-007 now reflects schedule slip risk surface. Audit trail: applied per Stella triage on Susan\'s roadblock email of 13 May 0938.', source: 'Stella Triage · PROJ-001' });
    }
    historyContent = 'Risk <strong>R-HSI-007</strong> escalated: score <span class="pill pill-old">12</span> → <span class="pill pill-new pill-risk">15</span>. Trend bumped from "down" to "up". Title updated to reflect OGC reviewer unavailability.';
    embedded.push({ kind: 'risk', id: 'R-HSI-007' });
    correspondence = { id: 'CORR-A-001', channel: 'Teams', to: 'C. Asante (Field Lab Cluster Lead)', subject: 'Risk R-HSI-007 escalated to 15 · OGC dependency', body: 'FYI — escalated R-HSI-007 from 12 to 15 per Stella triage of Susan\'s 0938 email. OGC reviewer out until 27 May. Mitigation tracking now active.' };
    embedded.push({ kind: 'correspondence', id: 'CORR-A-001' });
  } else if (kind === 'schedule') {
    fieldPatch.due = '03 Jun 2026';
    fieldPatch.statusLabel = 'On track · D-21 to review (re-scheduled)';
    fieldPatch.statusDetail = 'ADE-3 review re-scheduled to 03 Jun per OGC availability. FY26 Q3 funding line preserved.';
    fieldPatch.progress = 75;
    if (proj && proj.criticalPath) {
      const cp = proj.criticalPath.map((m) => ({ ...m }));
      const ade3 = cp.find((m) => m.name && m.name.indexOf('ADE-3 review') === 0);
      if (ade3) {
        ade3.date = '03 Jun 2026';
        ade3.name = 'ADE-3 review · IRB (re-scheduled)';
        ade3.upcomingDetails = { ...(ade3.upcomingDetails || {}) };
        ade3.upcomingDetails.summary = 'Re-scheduled from 22 May to 03 Jun per OGC reviewer availability (returns 27 May). 7+ days for cross-walk + IRB packet finalization. FY26 Q3 funding line preserved.';
        ade3.upcomingDetails.source = 'Stella Triage · auto';
        ade3.upcomingDetails.sourceIcon = SPARK_SVG;
        ade3.upcomingDetails.owner = 'Susan Williamson';
      }
      fieldPatch.criticalPath = cp;
    }
    historyContent = 'ADE-3 review date changed: <span class="pill pill-old">22 May 2026</span> → <span class="pill pill-new">03 Jun 2026</span>. FY26 Q3 funding line preserved (15 Jun obligation deadline). <em>Critical path milestone auto-updated.</em>';
    correspondence = { id: 'CORR-B-001', channel: 'Outlook Email', to: 'Susan Williamson, Division Chief', subject: 'RE: Roadblock — RAVEn ADE-3 stakeholder dependency closure delayed', body: 'Susan — confirming response to your 0938 note: (a) Recommend re-scheduling to 03 Jun rather than proceeding in absentia (cleaner audit trail). (b) 12-day slip preserves FY26 Q3 funding line (15 Jun deadline). (c) D. Reyes picking up cross-walk. Calendar invite for IRB convened 03 Jun coming separately.' };
    embedded.push({ kind: 'correspondence', id: 'CORR-B-001' });
  } else if (kind === 'personnel') {
    historyContent = '<strong>D. Reyes</strong> reassigned from TECS-Mod sprint to OGC cross-walk pickup (8 hr/wk through 27 May). <strong>B. Liu</strong> picks up Reyes\u2019 security review tasks for the sprint.';
    correspondence = { id: 'CORR-C-001', channel: 'Teams', to: 'D. Reyes, B. Liu, J. Lamb', subject: 'Reassignment effective today · OGC cross-walk pickup', body: 'Team — effective immediately, D. Reyes shifts 8 hr/wk through 27 May from TECS-Mod sprint to OGC cross-walk for RAVEn ADE-3. B. Liu absorbs Reyes\' security review tasks. Logged in project history. Questions in 1:1 today.' };
    embedded.push({ kind: 'correspondence', id: 'CORR-C-001' });
  } else if (kind === 'task') {
    _injectTask({ id: 'TK-070', title: 'Response to Susan re: RAVEn ADE-3 roadblock · cover (a/b/c) + latency follow-up', due: 'Today COB', priority: 'Highest' }, 'Stella Triage · PROJ-001', { date: today, recommendation: 'Stella Triage Rec D', summary: 'Task generated from Susan\'s 0938 roadblock email. Cover all three asks (a) review proceeding decision, (b) min slip, (c) cross-walk pickup. Plus latency follow-up.', outcome: 'Task added to queue at top. Linked to PROJ-001. Cross-reference: see correspondence to Susan logged in this triage session.', source: 'Stella Triage · PROJ-001' });
    if (rel.tasks && !rel.tasks.includes('TK-070')) rel.tasks.push('TK-070');
    historyContent = 'New task <strong>TK-070</strong> created: <em>"Response to Susan re: RAVEn ADE-3 roadblock · cover (a/b/c) + latency follow-up"</em>. Due COB today. Linked to PROJ-001.';
    embedded.push({ kind: 'task', id: 'TK-070' });
  }

  if (correspondence) {
    _injectCorrespondence(correspondence, letter, 'PROJ-001', today);
    if (!rel.correspondence) rel.correspondence = [];
    if (!rel.correspondence.includes(correspondence.id)) rel.correspondence.push(correspondence.id);
  }

  if (proj) {
    const histEntry: HistoryEntry = { date: today, type: 'ai-recommendation', actor: 'Stella', actorRole: 'Auto-triage', content: historyContent, source: 'Stella Triage · auto', sourceIcon: SPARK_SVG, embedded: embedded.length ? embedded : null };
    _patchProject('PROJ-001', { ...fieldPatch, relatedItems: rel, history: [histEntry, ...(proj.history || [])], _justUpdated: true });
  }

  // modal UI feedback (parity with index.html:20532-20548)
  const recEl = document.getElementById('triage-rec-' + letter);
  if (recEl) {
    recEl.classList.add('applied');
    const btn = document.getElementById('triage-rec-' + letter + '-btn') as HTMLButtonElement | null;
    if (btn) { btn.innerHTML = CHECK_SVG + ' Applied'; btn.disabled = true; }
    const lbl = document.getElementById('triage-rec-' + letter + '-label'); if (lbl) lbl.innerHTML = 'Applied ' + CHECK_SVG;
    const pills = (embedded || []).map((it) => _embedPillHtml(it));
    if (kind === 'schedule') pills.push(`<span class="embed-pill project" ${_tc('triageOpenProject', 'PROJ-001')}><span class="embed-pill-ico">▤</span>PROJ-001</span>`);
    if (pills.length) {
      let row = recEl.querySelector('.triage-rec-created');
      if (!row) { row = document.createElement('div'); row.className = 'triage-rec-created'; const foot = recEl.querySelector('.triage-rec-foot'); if (foot) recEl.insertBefore(row, foot); else recEl.appendChild(row); }
      row.innerHTML = '<span class="triage-rec-created-l">' + CHECK_SVG + ' Created / updated — click to open:</span>' + pills.join('');
    }
  }

  _activity({ status: 'ok', label: 'Stella Triage · recommendation ' + letter + ' applied', detail: historyContent.replace(/<[^>]+>/g, '').substring(0, 90), dur: 'manual · <1s' });
  // (UX audit #2) monolith globals window.projectsSave / window.aiRenderProjectsBoard
  // were never ported — the guarded calls silently no-opped. The store patch above
  // already re-renders the board, and the React port has no PROJECTS persistence layer.
}

function projectsApplyAllRecs(): void {
  ['A:risk', 'B:schedule', 'C:personnel', 'D:task'].forEach((p) => {
    const [letter, kind] = p.split(':');
    const btn = document.getElementById('triage-rec-' + letter + '-btn') as HTMLButtonElement | null;
    if (btn && !btn.disabled) projectsApplyRec(letter, kind);
  });
}

// ═════════════════════════════════════════════════════════════════════════════
// LIVE AI triage (POST /api/ai/triage) — the connected replacement for the canned
// scenarios. Any email (a live Graph message OR one of the demo emails, whose
// body we still feed as input) is sent to the server-side model, which returns
// detected items + recommended actions. We mint deterministic artifact ids from
// that response and hand it to the existing triageEmail() renderer so the full
// Apply / Undo / Apply-all / badge pipeline works unchanged. "Live AI only": on
// any failure the modal shows an error — there is no silent demo fallback.
// ═════════════════════════════════════════════════════════════════════════════
let _aiTriageSeq = 0;

function _priorityToRisk(priority: string): { score: number; level: string } {
  if (priority === 'Highest') return { score: 16, level: 'High' };
  if (priority === 'High') return { score: 12, level: 'High' };
  if (priority === 'Low') return { score: 4, level: 'Low' };
  return { score: 9, level: 'Medium' };
}

const _DETECTED_ICO: Record<string, string> = {
  risk: RISK_ICO, roadblock: ROADBLOCK_ICO, compliance: ROADBLOCK_ICO,
  personnel: PEOPLE_ICO, schedule: '⧉', finance: '◈',
};

function _initialsOf(name: string): string {
  const p = String(name || '').trim().split(/\s+/);
  return (((p[0] || '')[0] || '') + ((p[p.length - 1] || '')[0] || '')).toUpperCase() || '—';
}
function _decodeEntities(s: string): string {
  const named: Record<string, string> = {
    '&nbsp;': ' ', '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"',
    '&#39;': "'", '&apos;': "'", '&mdash;': '—', '&ndash;': '–',
    '&hellip;': '…', '&bull;': '•',
  };
  return String(s || '')
    .replace(/&(nbsp|amp|lt|gt|quot|#39|apos|mdash|ndash|hellip|bull);/g, (m) => named[m] || m)
    .replace(/&#(\d{1,6});/g, (_match, n) => {
      const c = Number(n);
      return c > 0 && c < 0x110000 ? String.fromCodePoint(c) : '';
    });
}
function _plainToHtml(s: string): string {
  // SECURITY: Graph email bodies are externally controlled — never inject raw
  // HTML into the DOM. Reduce to inert text: turn structural tags into newlines,
  // strip ALL remaining tags, DECODE entities (so &nbsp;/&amp; show as text, not
  // literal codes), then pbEsc — which RE-escapes any decoded < > & so a decoded
  // "<script>" renders as inert text (single pass — no recursive-decode hole) —
  // then re-add <br>. Live bodies carry no markup; demo bodies keep line breaks.
  const str = _decodeEntities(
    String(s || '')
      .replace(/<\s*br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|li|tr|h[1-6])>/gi, '\n')
      .replace(/<[^>]+>/g, '')
  );
  return pbEsc(str).replace(/\n{2,}/g, '\n').replace(/\n/g, '<br>');
}

// Turn the validated /api/ai/triage response into a TRIAGE_EMAILS-shaped entry
// (minted ids + mutations) that triageEmail() can render and apply.
function _buildLiveTriageEntry(email: LiveTriageEmail, data: LiveTriageResponse): TriageEmail {
  // The server returns targetProject only when the model picked a candidate id
  // verbatim, and null when none clearly applies. Honour the null: falling back
  // to the first project in the list routes real Jira/task/risk artifacts to an
  // unrelated contract purely by array order.
  const targetProject = data.targetProject || null;
  const detected: DetectedItem[] = (data.detected || []).map((d) => ({
    type: d.type || 'risk',
    ico: _DETECTED_ICO[d.type || ''] || SPARK_SVG,
    label: d.label || '',
    desc: d.desc || '',
  }));
  const recs: Recommendation[] = (data.recommendations || []).map((r, i) => {
    const letter = String.fromCharCode(65 + i); // A, B, C, …
    const seq = ++_aiTriageSeq;
    const mutations: Mutations = {};
    const impact: string[] = [];
    if (r.type === 'jira') {
      mutations.newJira = { id: 'Stella-JIRA-' + seq, summary: r.summary || r.title || '', type: 'Task', priority: r.priority || 'Medium', status: 'To Do', assignee: r.owner ? _initialsOf(r.owner) : '—', points: 3 };
      impact.push('jira · new');
    } else if (r.type === 'risk') {
      const sc = _priorityToRisk(r.priority || '');
      mutations.newRisk = { id: 'Stella-RISK-' + seq, title: r.summary || r.title || '', score: sc.score, level: sc.level, trend: 'up', owner: r.owner || 'Unassigned', category: 'Stella Triage' };
      impact.push('risk · new');
    } else if (r.type === 'correspondence') {
      mutations.correspondence = { id: 'Stella-CORR-' + seq, channel: 'Outlook Email', to: r.owner || email.from || 'Recipient', subject: 'RE: ' + (email.subject || ''), body: r.summary || r.detail || '' };
      impact.push('corr · new');
    } else {
      mutations.newTask = { id: 'Stella-TASK-' + seq, title: r.title || '', due: r.due || 'TBD', priority: r.priority || 'Medium' };
      impact.push('task · new');
    }
    if (targetProject) impact.push('project · ' + targetProject);
    // triageEmail() injects rec.detail as raw HTML; the model output is derived
    // from an untrusted email (prompt-injection surface), so escape it here.
    return { letter, title: r.title || 'Recommended action', detail: pbEsc(r.detail || ''), impact, mutations };
  });
  return {
    targetProject,
    modalTitle: BOLT_SVG + ' Stella Email Triage · ' + pbEsc((email.subject || '').substring(0, 60)),
    // No model id in the badge — the UI names no vendor or model.
    flagged: 'live · Stella',
    // from/subject are attacker-controlled Graph headers; triageEmail() renders
    // them into the modal via innerHTML (subject fully raw, from only <>-stripped),
    // so pre-escape both here. The trusted demo path builds its own entries and is
    // unaffected.
    from: pbEsc(email.from || 'Unknown sender'),
    subject: pbEsc(email.subject || '(no subject)'),
    bodyHtml: _plainToHtml(email.body || email.snippet || ''),
    detected,
    recs,
  };
}

// Open the triage modal for an email, generating recommendations live from the
// model. `email` = { from, subject, body }. Reuses the demo email's body when
// invoked from a canned inbox item; uses the live Graph body for real mail.
const TRIAGE_STEPS = ['Processing email', 'Analyzing text', 'Generating recommendations'];

async function triageEmailLive(email?: LiveTriageEmail): Promise<void> {
  const e = email || {};

  // Stale-response guard: bail if a newer triage started or the user closed the
  // loading modal (#mo gone) before this request resolved — so a late reply can't
  // pop an unexpected modal back open, and stray step timers stop advancing too.
  const myToken = ((triageEmailLive as any)._token = ((triageEmailLive as any)._token || 0) + 1);
  const stale = () => myToken !== (triageEmailLive as any)._token || !document.getElementById('mo');

  const steps = TRIAGE_STEPS.map((label, i) => `<div class="triage-step" data-step="${i}">
      <span class="triage-step-dot"><span class="triage-step-check">${CHECK_SVG}</span></span>
      <span class="triage-step-label">${pbEsc(label)}</span>
    </div>`).join('<span class="triage-step-line"></span>');
  const loading = `<div class="triage-modal"><div class="triage-email">
    <div class="triage-email-h">${SPARK_SVG} Analyzing with Stella…</div>
    <div class="triage-email-from">From: ${pbEsc(e.from || '')}</div>
    <div class="triage-email-subj">Subject: ${pbEsc(e.subject || '')}</div>
  </div>
  <div class="triage-progress">${steps}</div>
  <div class="triage-progress-note">Live model · routes to your projects</div></div>`;
  _openXlModal(BOLT_SVG + ' Stella Email Triage', loading, `<button class="btn btn--sm" ${_tc('closeMo')}>Close</button>`);

  // Cosmetic step pacing — the API call is a single round trip with no
  // progress events, so this just paces the three labels above against a
  // typical response time. It never blocks or races the real fetch below;
  // `stale()` stops it once the modal's gone or a newer triage superseded it.
  const setActiveStep = (idx: number) => {
    if (stale()) return;
    document.querySelectorAll('#mo .triage-step').forEach((node, i) => {
      node.classList.toggle('is-active', i === idx);
      node.classList.toggle('is-done', i < idx);
    });
  };
  setActiveStep(0);
  const stepTimers = [setTimeout(() => setActiveStep(1), 900), setTimeout(() => setActiveStep(2), 2200)];

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 30000);
  try {
    const projects = ((window as any).PROJECTS || []).map((p: Project) => ({ id: p.id, name: p.name }));
    // Send a TAG-STRIPPED plain-text body, not the raw email HTML. The model only
    // needs the prose, and raw email markup (<script>, onerror=, links) trips the
    // WAF's managed XSS/injection rules in front of prod (a real 403 seen live) —
    // and stripping also shrinks the prompt-injection surface.
    const plainBody = String(e.body || e.snippet || '')
      .replace(/<\s*(script|style)[^>]*>[\s\S]*?<\/\s*\1\s*>/gi, ' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 12000);
    // Base64-encode the (already tag-stripped) body. Real Outlook message text
    // still trips the prod WAF's managed XSS/injection rules after stripping, so we
    // send it opaque and the server decodes it (verified live: base64 body -> 200).
    const _bytes = new TextEncoder().encode(plainBody);
    let _bin = '';
    for (const b of Array.from(_bytes)) _bin += String.fromCharCode(b);
    const bodyB64 = btoa(_bin);
    const resp = await pbAuthedFetch('/api/ai/triage', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal: controller.signal,
      body: JSON.stringify({ email: { from: e.from, subject: e.subject, bodyB64 }, projects }),
    });
    if (stale()) return;
    if (!resp.ok) {
      const detail = resp.status === 503 ? 'Stella is not configured.'
        : resp.status === 502 ? 'The model did not return a usable result. Try again.'
        : 'Triage request failed (HTTP ' + resp.status + ').';
      _renderTriageError(e, detail);
      return;
    }
    const data = await resp.json().catch(() => null) as LiveTriageResponse | null;
    if (stale()) return;
    if (!data || !data.ok || !Array.isArray(data.recommendations) || !data.recommendations.length) {
      _renderTriageError(e, 'The model returned no recommendations for this email.');
      return;
    }
    const dynKey = 'ai-' + (++_aiTriageSeq);
    TRIAGE_EMAILS[dynKey] = _buildLiveTriageEntry(e, data);
    triageEmail(dynKey); // reuse the full renderer + Apply/Undo/Apply-all pipeline
  } catch (err) {
    if (stale()) return;
    _renderTriageError(e, (err as Error).name === 'AbortError' ? 'The Stella request timed out.' : 'Could not reach Stella.');
  } finally {
    clearTimeout(timer);
    stepTimers.forEach(clearTimeout);
  }
}

function _renderTriageError(email: LiveTriageEmail, message: string): void {
  const e = email || {};
  const body = `<div class="triage-modal"><div class="triage-email">
    <div class="triage-email-h">${RISK_ICO} Stella triage unavailable</div>
    <div class="triage-email-from">From: ${pbEsc(e.from || '')}</div>
    <div class="triage-email-subj">Subject: ${pbEsc(e.subject || '')}</div>
  </div>
  <div style="padding:22px 6px;font-family:var(--ff)">
    <div style="font-size:13px;font-weight:700;color:var(--navy);margin-bottom:6px">Could not generate recommendations</div>
    <div style="font-size:12.5px;color:var(--t2);line-height:1.5">${pbEsc(message)}</div>
  </div></div>`;
  _openXlModal(BOLT_SVG + ' Stella Email Triage', body,
    `<button class="btn btn--sm" ${_tc('closeMo')}>Close</button><button class="btn btn--primary btn--sm" ${_tc('closeMo')}>Dismiss</button>`);
  _activity({ status: 'warn', label: 'Stella Triage · unavailable', detail: message, dur: 'Stella · error' });
}

// Attach the globals that the modal's inline onclick="" handlers resolve.
export function installTriageEngine(): void {
  if (typeof window === 'undefined') return;
  _installTriageClickDelegation();
  (window as any).triageEmail = triageEmail;
  (window as any).triageSkipRec = triageSkipRec;
  (window as any).triageApplyRec = triageApplyRec;
  (window as any).triageUndoRec = triageUndoRec;
  (window as any).triageApplyAll = triageApplyAll;
  (window as any).triageOpenProject = triageOpenProject;
  (window as any).triageOpenItem = triageOpenItem;
  (window as any).pbGoToWorkspace = () => _pbNavigate('/workspace');
  (window as any).triageMarkInboxState = triageMarkInboxState;
  (window as any).triageUpdateBadges = triageUpdateBadges;
  (window as any).projectsTriageDemo = projectsTriageDemo;
  (window as any).projectsApplyRec = projectsApplyRec;
  (window as any).projectsApplyAllRecs = projectsApplyAllRecs;
  // InboxPage's window.triageOpenCenter (index.html:21148) reads these two data
  // maps directly to render all 7 scenario rows + their applied/partial state —
  // it computes names/counts itself from window.PROJECTS, so only the raw data
  // needs to be reachable here.
  (window as any).TRIAGE_EMAILS = TRIAGE_EMAILS;
  (window as any).TRIAGE_DONE = TRIAGE_DONE;
  // Live AI triage entrypoint — inbox items (demo + live Graph mail) route here.
  (window as any).triageEmailLive = triageEmailLive;
}

export { triageEmail, triageEmailLive, projectsTriageDemo };
